import React, { createContext, useContext, useEffect, useCallback } from 'react';
import { usePomodoro, WorkSessionInfo } from '@/hooks/usePomodoro';
import { useCreateWorkShift } from '@/hooks/useWorkShifts';
import { useAuth } from '@/contexts/AuthContext';

type PomodoroContextValue = ReturnType<typeof usePomodoro>;

const PomodoroContext = createContext<PomodoroContextValue | null>(null);

export const PomodoroProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const pomodoro = usePomodoro();
  const createWorkShift = useCreateWorkShift();
  const { isAuthenticated } = useAuth();

  const handleWorkSessionComplete = useCallback((info: WorkSessionInfo) => {
    if (!isAuthenticated) return;

    const notes = info.project
      ? `[${info.project.project_code}] ${info.project.project_name}`
      : '';

    createWorkShift.mutate({
      date: info.date,
      shift_type: 'pomodoro',
      start_time: info.startTime,
      end_time: info.endTime,
      break_duration: 0,
      notes,
    });
  }, [isAuthenticated, createWorkShift]);

  useEffect(() => {
    pomodoro.setOnWorkSessionComplete(handleWorkSessionComplete);
    return () => pomodoro.setOnWorkSessionComplete(null);
  }, [handleWorkSessionComplete, pomodoro.setOnWorkSessionComplete]);

  return (
    <PomodoroContext.Provider value={pomodoro}>
      {children}
    </PomodoroContext.Provider>
  );
};

export function usePomodoroContext(): PomodoroContextValue {
  const ctx = useContext(PomodoroContext);
  if (!ctx) {
    throw new Error('usePomodoroContext must be used within a PomodoroProvider');
  }
  return ctx;
}
